import slugify from 'slugify';

export const toSlug = (string: string) =>
  slugify(string, {
    lower: true,
    remove: /[*+~.,()'"!:@]/g,
  }).replace(/[\/_]/g, '-');

/** Backstage entity-name cap: lowercase `[a-z0-9._-]`, ≤63, alnum-bounded. */
export const MAX_ENTITY_NAME_LENGTH = 63;

/**
 * Names that cannot be used as an entity name because a catalog URL or an
 * entity ref built from them would address something else. Kept as one
 * exported constant so a caller can report the whole set rather than probing
 * it a name at a time.
 */
export const RESERVED_ENTITY_NAMES: readonly string[] = [
  'default',
  'all',
  'none',
  'new',
  'edit',
  'create',
  'delete',
];

/** Why {@link validateEntityName} rejected a name. */
export type EntityNameProblem =
  | 'empty'
  | 'too-long'
  | 'reserved'
  | 'invalid-characters';

export type EntityNameValidation =
  | { valid: true; name: string }
  | { valid: false; problem: EntityNameProblem; name: string };

/**
 * Check that `value` is usable as a Backstage entity name, returning a
 * structured reason instead of throwing so a caller validating many names can
 * report every bad one in a single pass rather than aborting on the first.
 *
 * {@link toSlug} performs none of these checks — it transliterates and folds
 * separators but will happily return an empty string, an over-length string, or
 * a reserved word — so this must be called explicitly rather than assumed to
 * have happened during slugification.
 */
export const validateEntityName = (value: string): EntityNameValidation => {
  const name = value.trim();
  if (name.length === 0) {
    return { valid: false, problem: 'empty', name };
  }
  if (name.length > MAX_ENTITY_NAME_LENGTH) {
    return { valid: false, problem: 'too-long', name };
  }
  if (!/^[a-z0-9]([a-z0-9._-]*[a-z0-9])?$/.test(name)) {
    return { valid: false, problem: 'invalid-characters', name };
  }
  if (RESERVED_ENTITY_NAMES.includes(name)) {
    return { valid: false, problem: 'reserved', name };
  }
  return { valid: true, name };
};
